/**
 * The share routes (server shares-routes.handler.ts), for the dialog, Files and Shared
 * with me. Never throws: every answer is { ok, value } or a refusal code read from the
 * body's `error` field, never from its text.
 */
import type {
  AddPersonResult,
  GeneralAccess,
  ShareState,
  SharedWithMeItem,
  SuggestedPerson,
} from "@bash/shared";
import { apiUrl } from "@/lib/api/base";
import { getOptionalAccessToken } from "@/lib/api/client";
import {
  isAccountBlockedResponse,
  signalAccountBlocked,
} from "@/lib/account-block";

export type ShareRefusal =
  "not_found" | "rate_limited" | "invalid_email" | "unavailable";
export type ShareCall<T> =
  { ok: true; value: T } | { ok: false; code: ShareRefusal };

const REFUSALS: ReadonlySet<ShareRefusal> = new Set<ShareRefusal>([
  "not_found",
  "rate_limited",
  "invalid_email",
]);

async function call<T>(
  path: string,
  init: RequestInit = {}
): Promise<ShareCall<T>> {
  try {
    const token = await getOptionalAccessToken();
    if (!token) return { ok: false, code: "unavailable" };
    const res = await fetch(apiUrl(path), {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body != null ? { "Content-Type": "application/json" } : {}),
      },
    });
    if (res.status === 403 && (await isAccountBlockedResponse(res)))
      signalAccountBlocked();
    const body = (await res.json().catch(() => null)) as
      (T & { error?: unknown }) | null;
    if (res.ok && body) return { ok: true, value: body };
    const raw = body && typeof body === "object" ? body.error : undefined;
    return {
      ok: false,
      code:
        typeof raw === "string" && REFUSALS.has(raw as ShareRefusal)
          ? (raw as ShareRefusal)
          : "unavailable",
    };
  } catch {
    return { ok: false, code: "unavailable" };
  }
}

const path = (id: string) => `/api/shares/${encodeURIComponent(id)}`;

export const fetchShare = (id: string) => call<ShareState>(path(id));
export const addPersonCall = (id: string, email: string) =>
  call<AddPersonResult>("/api/shares", {
    method: "POST",
    body: JSON.stringify({ id, email }),
  });
export const removePersonCall = (id: string, userId: string) =>
  call<{ removed: boolean }>(
    `${path(id)}/people/${encodeURIComponent(userId)}`,
    { method: "DELETE" }
  );
export const setGeneralAccessCall = (id: string, general: GeneralAccess) =>
  call<{ general: GeneralAccess }>(`${path(id)}/general`, {
    method: "PATCH",
    body: JSON.stringify({ general }),
  });
export const fetchSharedWithMe = () =>
  call<{ items: SharedWithMeItem[] }>("/api/shares/with-me");
export const fetchShareSuggestions = () =>
  call<{ people: SuggestedPerson[] }>("/api/shares/suggestions");
export const fetchShareMarks = () =>
  call<{ marks: Record<string, number> }>("/api/shares/marks");

/** The site's front door: the configured site URL, else this origin. */
function siteOrigin(): string {
  return (
    process.env.NEXT_PUBLIC_SITE_URL?.trim().replace(/\/+$/, "") ||
    window.location.origin
  );
}

/** One of this person's own live invite codes as a link (/i/<code>), or null when they have none. */
export async function fetchInviteLink(): Promise<string | null> {
  const r = await call<{
    invites?: Array<{ code: string; disabled_at: string | null }>;
  }>("/api/invites/mine");
  if (!r.ok) return null;
  const live = (r.value.invites ?? []).find((i) => i.disabled_at === null);
  return live ? `${siteOrigin()}/i/${encodeURIComponent(live.code)}` : null;
}
